import {
  Period,
  Product,
  SubscriptionService,
} from "@service/subscription/subscription.service";
import {
  CustomerInfo,
  ErrorCode,
  Offerings,
  Price,
  Purchases,
  PurchasesError,
} from "@revenuecat/purchases-js";
import i18n from 'i18next';

const API_KEY = process.env.REACT_APP_REVENUECAT_PUBLIC_API_KEY;

export class RevenuecatSubscriptionService implements SubscriptionService {
  public static readonly instance = new RevenuecatSubscriptionService();

  private customerInfo: CustomerInfo | null = null;
  private offerings: Offerings | null = null;

  private constructor() {}

  public async getProducts(): Promise<Product[]> {
    if (!this.customerInfo) {
      return [];
    }

    try {
      if (!this.offerings) {
        this.offerings = await Purchases.getSharedInstance().getOfferings();
      }
      return (
        this.offerings.current?.availablePackages.map((p) => ({
          id: p.identifier,
          price: {
            ...p.rcBillingProduct.currentPrice,
            formattedPrice: this.formatAmount(
              p.rcBillingProduct.currentPrice.amount / 100,
              p.rcBillingProduct.currentPrice.currency,
            ),
            monthlyFormattedPrice: this.getMonthlyFormattedPrice(
              p.rcBillingProduct.normalPeriodDuration,
              p.rcBillingProduct.currentPrice,
            ),
          },
          period: this.convertPeriodDuration(
            p.rcBillingProduct.normalPeriodDuration,
          ),
        })) || []
      );
    } catch (e) {
      console.error(e);
    }

    return [];
  }

  public async subscribe(product: Product, email?: string): Promise<boolean> {
    if (!this.customerInfo) {
      return false;
    }

    try {
      if (!this.offerings) {
        this.offerings = await Purchases.getSharedInstance().getOfferings();
      }
      const rcPackage = this.offerings.current?.availablePackages?.find(
        (p) => p.identifier === product.id,
      );
      if (!rcPackage) {
        return false;
      }
      const { customerInfo } = await Purchases.getSharedInstance().purchase({
        rcPackage,
        customerEmail: email,
        selectedLocale: i18n.language || 'en-US'
      });
      this.customerInfo = customerInfo;
      return true;
    } catch (e) {
      if (
        e instanceof PurchasesError &&
        e.errorCode === ErrorCode.UserCancelledError
      ) {
        // User cancelled the purchase process, don't do anything
      } else {
        console.error(e);
      }
    }
    return false;
  }

  async cancelSubscription(): Promise<void> {
    const url = this.customerInfo?.managementURL;
    if (!url) {
      throw new Error("Management url is missing");
    }
    window.open(url, "_blank")?.focus();
  }

  public getSubscriptions(): string[] {
    if (!this.customerInfo) {
      return [];
    }

    return Array.from(this.customerInfo.activeSubscriptions);
  }

  async init(userId: string): Promise<boolean> {
    if (this.customerInfo) {
      return true;
    }
    if (!API_KEY) {
      throw new Error("Missing 'BILLING_API_KEY'");
    }
    try {
      Purchases.configure(API_KEY, userId);

      this.customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  destroy(): void {
    this.customerInfo = null;
  }

  private getMonthlyFormattedPrice(
    duration: string | null,
    price: Price,
  ): string | null {
    switch (duration) {
      case "P1M": {
        return this.formatAmount(price.amount / 100, price.currency);
      }
      case "P1Y": {
        return this.formatAmount(price.amount / 100 / 12, price.currency);
      }
      default: {
        return null;
      }
    }
  }

  private formatAmount(amount: number, currency: string): string {
    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    return formatter.format(amount);
  }

  private convertPeriodDuration(duration: string | null): Period | null {
    switch (duration) {
      case "P1M": {
        return "month";
      }
      case "P1Y": {
        return "year";
      }
      default: {
        return null;
      }
    }
  }
}
